import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { defaultRoute } from "@/components/RoleRoute";
import { GFS_LOGO_URL } from "@/lib/brand";
import { api, formatApiError, setAccessToken } from "@/lib/api";

export default function FirstLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = location.state?.token;

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!token) navigate("/login", { replace: true });
  }, [token, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    if (pwd !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/first-login-password", {
        change_password_token: token,
        new_password: pwd,
      });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data);
      navigate(defaultRoute(data.role, "Active"), { replace: true });
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Could not set password";
      if (/expired/i.test(msg)) setExpired(true);
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  if (!token) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-4" data-testid="first-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-[#D2E4D6]/70 p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src={GFS_LOGO_URL} alt="GreenForest Solutions"
               className="w-12 h-12 object-contain bg-white rounded-lg" />
          <div>
            <div className="text-sm font-semibold tracking-wide text-[#056839]">GreenForest Solutions</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3F3F3F]/60">PROcess Traceability</div>
          </div>
        </div>

        <h1 className="font-heading text-2xl font-semibold text-[#000000]">Set your password</h1>
        <p className="text-sm text-[#3F3F3F]/80 mt-1 mb-6">
          Welcome to PROcess. Create a permanent password to access your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#69A488] font-medium">New password</Label>
            <Input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Minimum 8 characters"
              required
              autoFocus
              data-testid="first-login-new-password"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#69A488] font-medium">Confirm password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your new password"
              required
              data-testid="first-login-confirm-password"
              className="mt-1 h-11"
            />
          </div>

          {err && (
            <div className="text-sm text-[#A0522D]" data-testid="first-login-error">
              {expired
                ? <>Session expired — please <Link to="/login" className="underline">log in again</Link>.</>
                : err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="first-login-submit"
            className="w-full h-11 gfs-bg-primary hover:bg-[#044E2A] text-white rounded-lg"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> Set password &amp; continue</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
